import { useState, useEffect, useRef } from 'react';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  Search, 
  Loader2, 
  FlaskConical, 
  Leaf, 
  Bug, 
  Droplets,
  AlertTriangle,
  CheckCircle2,
  ExternalLink
} from 'lucide-react';
import api from '@/lib/api';

/**
 * Composant de recherche EPHY avec autocomplétion
 * Style similaire à https://ephy.anses.fr
 */
export const EphySearchInput = ({ onSelect, placeholder = "Rechercher un produit EPHY..." }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  // Debounce search
  useEffect(() => {
    if (query.length < 2) {
      setResults([]);
      setShowDropdown(false);
      return;
    }

    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await api.get(`/ephy/search?q=${encodeURIComponent(query)}&limit=10`);
        setResults(res.data?.resultats || []);
        setShowDropdown(true);
      } catch (error) {
        console.error('Erreur recherche EPHY:', error);
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [query]);

  // Fermer le dropdown au clic extérieur
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target) &&
          inputRef.current && !inputRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getFonctionIcon = (fonction) => {
    switch (fonction?.toLowerCase()) {
      case 'herbicide': return <Leaf className="w-4 h-4 text-green-600" />;
      case 'fongicide': return <FlaskConical className="w-4 h-4 text-purple-600" />;
      case 'insecticide': return <Bug className="w-4 h-4 text-red-600" />;
      default: return <Droplets className="w-4 h-4 text-blue-600" />;
    }
  };

  const getFonctionColor = (fonction) => {
    switch (fonction?.toLowerCase()) {
      case 'herbicide': return 'bg-green-100 text-green-800';
      case 'fongicide': return 'bg-purple-100 text-purple-800';
      case 'insecticide': return 'bg-red-100 text-red-800';
      default: return 'bg-blue-100 text-blue-800';
    }
  };

  const handleSelect = (produit) => {
    setQuery(produit.nom_commercial);
    setShowDropdown(false);
    onSelect?.(produit);
  };

  return (
    <div className="relative">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          ref={inputRef}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={placeholder}
          className="pl-10 pr-10"
          onFocus={() => results.length > 0 && setShowDropdown(true)}
        />
        {loading && (
          <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-muted-foreground" />
        )}
      </div>

      {showDropdown && results.length > 0 && (
        <div 
          ref={dropdownRef}
          className="absolute z-50 w-full mt-1 bg-card border border-border rounded-lg shadow-lg"
        >
          <ScrollArea className="max-h-80">
            <div className="p-1">
              {results.map((produit, idx) => (
                <button
                  key={idx}
                  onClick={() => handleSelect(produit)}
                  className="w-full text-left px-3 py-2 hover:bg-muted rounded-md transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {getFonctionIcon(produit.fonction)}
                      <div>
                        <p className="font-medium">{produit.nom_commercial}</p>
                        <p className="text-xs text-muted-foreground">
                          AMM: {produit.numero_amm} • {produit.firme}
                        </p>
                      </div>
                    </div>
                    <Badge className={getFonctionColor(produit.fonction)}>
                      {produit.fonction}
                    </Badge>
                  </div>
                  {produit.substances_actives?.length > 0 && (
                    <p className="text-xs text-muted-foreground mt-1 ml-6">
                      {produit.substances_actives.map(s => s.nom).join(', ')}
                    </p>
                  )}
                </button>
              ))}
            </div>
          </ScrollArea>
          <div className="border-t border-border p-2 text-xs text-muted-foreground text-center">
            Données EPHY - ANSES
          </div>
        </div>
      )}
    </div>
  );
};


/**
 * Composant de saisie de cible avec suggestions
 */
export const CibleSuggestInput = ({ value, onChange, placeholder = "Ex: Mildiou, Ray-grass" }) => {
  const [query, setQuery] = useState(value || '');
  const [suggestions, setSuggestions] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    setQuery(value || '');
  }, [value]);

  useEffect(() => {
    if (query.length < 2) {
      setSuggestions([]);
      setShowDropdown(false);
      return;
    }

    const timer = setTimeout(async () => {
      try {
        const res = await api.get(`/ephy/cibles/suggestions?q=${encodeURIComponent(query)}`);
        setSuggestions(res.data?.suggestions || []);
        setShowDropdown(true);
      } catch (error) {
        // Fallback local si l'API n'est pas disponible
        const localSuggestions = getLocalCibleSuggestions(query);
        setSuggestions(localSuggestions);
        setShowDropdown(localSuggestions.length > 0);
      }
    }, 200);

    return () => clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target) &&
          inputRef.current && !inputRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInputChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    onChange?.(val);
  };

  const handleSelect = (suggestion) => {
    setQuery(suggestion.nom);
    onChange?.(suggestion.nom);
    setShowDropdown(false);
  };

  const getCategorieColor = (cat) => {
    switch (cat) {
      case 'adventices': return 'bg-green-100 text-green-700';
      case 'maladies': return 'bg-purple-100 text-purple-700';
      case 'ravageurs': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <div className="relative">
      <Input
        ref={inputRef}
        value={query}
        onChange={handleInputChange}
        placeholder={placeholder}
        onFocus={() => suggestions.length > 0 && setShowDropdown(true)}
      />

      {showDropdown && suggestions.length > 0 && (
        <div 
          ref={dropdownRef}
          className="absolute z-50 w-full mt-1 bg-card border border-border rounded-lg shadow-lg max-h-48 overflow-y-auto"
        >
          <div className="p-1">
            {suggestions.map((suggestion, idx) => (
              <button
                key={idx}
                onClick={() => handleSelect(suggestion)}
                className="w-full text-left px-3 py-2 hover:bg-muted rounded-md transition-colors flex items-center justify-between"
              >
                <span>{suggestion.nom}</span>
                <Badge variant="outline" className={getCategorieColor(suggestion.categorie)}>
                  {suggestion.categorie}
                </Badge>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};


/**
 * Fiche produit EPHY détaillée (style ANSES)
 */
export const EphyProduitCard = ({ numeroAmm, onClose }) => {
  const [produit, setProduit] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProduit = async () => {
      try {
        const res = await api.get(`/ephy/produit/${numeroAmm}`);
        setProduit(res.data);
      } catch (error) {
        console.error('Erreur chargement produit EPHY:', error);
      } finally {
        setLoading(false);
      }
    };
    if (numeroAmm) fetchProduit();
  }, [numeroAmm]);

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    );
  }

  if (!produit) {
    return (
      <div className="p-4 text-center text-muted-foreground">
        Produit non trouvé
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* En-tête */}
      <div className="flex items-start justify-between border-b pb-4">
        <div>
          <h2 className="text-xl font-bold">{produit.nom_commercial}</h2>
          <p className="text-sm text-muted-foreground">{produit.firme}</p>
        </div>
        <div className="text-right">
          <Badge variant={produit.statut === 'autorise' ? 'default' : 'destructive'}>
            {produit.statut === 'autorise' ? (
              <><CheckCircle2 className="w-3 h-3 mr-1" /> Autorisé</>
            ) : (
              <><AlertTriangle className="w-3 h-3 mr-1" /> Retiré</>
            )}
          </Badge>
          <p className="text-xs text-muted-foreground mt-1">AMM: {produit.numero_amm}</p>
        </div>
      </div>

      {/* Substances actives */}
      <div>
        <h3 className="font-semibold mb-2">Substances actives</h3>
        <div className="space-y-1">
          {produit.substances_actives?.map((s, idx) => (
            <div key={idx} className="flex items-center justify-between text-sm bg-muted/50 px-3 py-2 rounded">
              <span className="font-medium">{s.nom}</span>
              <span className="text-muted-foreground">{s.concentration} {s.unite}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Infos générales */}
      <div className="grid grid-cols-2 gap-4 text-sm">
        <div>
          <p className="text-muted-foreground">Fonction</p>
          <p className="font-medium capitalize">{produit.fonction}</p>
        </div>
        <div>
          <p className="text-muted-foreground">Formulation</p>
          <p className="font-medium">{produit.formulation}</p>
        </div>
        <div>
          <p className="text-muted-foreground">Gamme d'usage</p>
          <p className="font-medium">{produit.gamme_usage}</p>
        </div>
      </div>

      {/* Conditions d'emploi */}
      {produit.conditions_emploi && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <h3 className="font-semibold mb-2 text-amber-800">Conditions d'emploi</h3>
          <div className="grid grid-cols-2 gap-3 text-sm">
            {produit.conditions_emploi.znt_aquatique && (
              <div>
                <p className="text-amber-700">ZNT Aquatique</p>
                <p className="font-bold text-amber-900">{produit.conditions_emploi.znt_aquatique} m</p>
              </div>
            )}
            {produit.conditions_emploi.znt_riverains && (
              <div>
                <p className="text-amber-700">ZNT Riverains</p>
                <p className="font-bold text-amber-900">{produit.conditions_emploi.znt_riverains} m</p>
              </div>
            )}
            {produit.conditions_emploi.delai_reentree && (
              <div>
                <p className="text-amber-700">Délai de réentrée</p>
                <p className="font-bold text-amber-900">{produit.conditions_emploi.delai_reentree} h</p>
              </div>
            )}
          </div>
          {produit.conditions_emploi.epi_requis?.length > 0 && (
            <div className="mt-3">
              <p className="text-amber-700 text-sm">EPI requis:</p>
              <div className="flex flex-wrap gap-1 mt-1">
                {produit.conditions_emploi.epi_requis.map((epi, idx) => (
                  <Badge key={idx} variant="outline" className="text-xs border-amber-400 text-amber-800">
                    {epi}
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Phrases de danger */}
      {produit.phrases_danger?.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <h3 className="font-semibold mb-2 text-red-800 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            Phrases de danger
          </h3>
          <div className="space-y-2">
            {produit.phrases_danger.map((phrase, idx) => (
              <div key={idx} className="text-sm">
                <span className="font-mono font-bold text-red-700">{phrase.code}</span>
                <span className="text-red-900 ml-2">{phrase.texte}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Usages autorisés */}
      {produit.usages?.length > 0 && (
        <div>
          <h3 className="font-semibold mb-2">Usages autorisés ({produit.usages.length})</h3>
          <ScrollArea className="h-48">
            <div className="space-y-2">
              {produit.usages.map((usage, idx) => (
                <div key={idx} className="bg-muted/50 p-3 rounded-lg text-sm">
                  <div className="flex items-center justify-between">
                    <span className="font-medium">{usage.culture}</span>
                    <Badge variant="outline">{usage.cible}</Badge>
                  </div>
                  <div className="grid grid-cols-3 gap-2 mt-2 text-xs text-muted-foreground">
                    <div>
                      <p>Dose max</p>
                      <p className="font-medium text-foreground">{usage.dose_max} {usage.unite_dose}</p>
                    </div>
                    <div>
                      <p>Applications</p>
                      <p className="font-medium text-foreground">{usage.nb_applications_max || '-'}</p>
                    </div>
                    <div>
                      <p>DAR</p>
                      <p className="font-medium text-foreground">{usage.dar ? `${usage.dar}j` : '-'}</p>
                    </div>
                  </div>
                  {usage.conditions && (
                    <p className="text-xs text-muted-foreground mt-2 italic">{usage.conditions}</p>
                  )}
                </div>
              ))}
            </div>
          </ScrollArea>
        </div>
      )}

      {/* Lien EPHY officiel */}
      <div className="border-t pt-4">
        <a
          href={`https://ephy.anses.fr/ppp/${produit.nom_commercial?.toLowerCase().replace(/\s+/g, '-')}`}
          target="_blank"
          rel="noopener noreferrer"
          className="text-sm text-blue-600 hover:underline flex items-center gap-1"
        >
          <ExternalLink className="w-3 h-3" />
          Voir sur EPHY officiel (ANSES)
        </a>
      </div>
    </div>
  );
};


// Suggestions locales de secours
function getLocalCibleSuggestions(query) {
  const cibles = {
    adventices: [
      "Ray-grass", "Vulpin", "Gaillet", "Chardon", "Liseron", "Rumex",
      "Chiendent", "Folle avoine", "Brome", "Pâturin", "Agrostide",
      "Véronique", "Stellaire", "Capselle", "Matricaire", "Coquelicot"
    ],
    maladies: [
      "Mildiou", "Oïdium", "Botrytis", "Septoriose", "Rouille",
      "Fusariose", "Alternariose", "Anthracnose", "Sclérotinia",
      "Rhizoctone", "Pythium", "Phytophthora", "Tavelure", "Moniliose"
    ],
    ravageurs: [
      "Pucerons", "Cicadelles", "Acariens", "Thrips", "Aleurodes",
      "Chenilles", "Pyrale", "Tordeuse", "Noctuelles", "Mouches",
      "Altises", "Charançons", "Criocères", "Doryphores", "Cochenilles"
    ]
  };

  const results = [];
  for (const [categorie, noms] of Object.entries(cibles)) {
    for (const nom of noms) {
      if (nom.toLowerCase().includes(query.toLowerCase())) {
        results.push({ nom, categorie, label: `${nom} (${categorie})` });
      }
    }
  }
  return results.slice(0, 10);
}

export default EphySearchInput;
